import type { PluginDto, Role, SkillDto, ToolDto } from '@agent-platform/shared';
import { hasRole } from '@agent-platform/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, Pencil, Trash2 } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { toast } from 'sonner';
import { EmptyState } from '@/components/empty-state';
import { ErrorAlert } from '@/components/error-alert';
import { ListSearch, NoMatches, useListSearch } from '@/components/list-search';
import { PageHeader } from '@/components/page-header';
import { PluginMarketplace } from '@/components/plugin-marketplace';
import { SkillsFolderImportDialog } from '@/components/skills-folder-import';
import { RequireTenant } from '@/components/require-tenant';
import { EditSkillDialog } from '@/components/skill-editor';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { api, ApiError, errorMessage } from '@/lib/api';
import { formatDate } from '@/lib/format';

function NewSkillDialog({ tenantId, tools }: { tenantId: string; tools: ToolDto[] }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [instructions, setInstructions] = useState('');
  const [toolIds, setToolIds] = useState<string[]>([]);
  const create = useMutation({
    mutationFn: () =>
      api<SkillDto>('/skills', {
        method: 'POST',
        body: { name, description, instructions, toolIds },
      }),
    onSuccess: (s) => {
      toast.success(`Skill ${s.name} created`);
      void queryClient.invalidateQueries({ queryKey: ['skills', tenantId] });
      setOpen(false);
    },
  });
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) create.reset();
      }}
    >
      <DialogTrigger asChild>
        <Button>New skill</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Create a skill</DialogTitle>
          <DialogDescription>
            Agents with this skill get its instructions (under “## Skill: name”) and its tools.
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-4"
          noValidate
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <ErrorAlert error={create.error} />
          <div className="grid gap-1.5">
            <Label htmlFor="skill-name">Name</Label>
            <Input
              id="skill-name"
              className="font-mono"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="refunds"
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="skill-description">Description</Label>
            <Input
              id="skill-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="skill-instructions">Instructions</Label>
            <Textarea
              id="skill-instructions"
              className="min-h-32"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
            />
          </div>
          <fieldset className="grid gap-1">
            <legend className="mb-1 text-sm font-medium">Tools</legend>
            {tools.length === 0 && <p className="text-muted-foreground text-sm">No tools yet.</p>}
            {tools.map((t) => (
              <label key={t.id} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={toolIds.includes(t.id)}
                  onChange={(e) =>
                    setToolIds((ids) =>
                      e.target.checked ? [...ids, t.id] : ids.filter((i) => i !== t.id),
                    )
                  }
                />
                <span className="font-mono">{t.name}</span>
              </label>
            ))}
          </fieldset>
          <DialogFooter>
            <Button type="submit" disabled={create.isPending}>
              Create skill
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const EXAMPLE_MANIFEST = JSON.stringify(
  {
    name: 'weather-pack',
    version: '1.0.0',
    description: 'Weather lookups',
    tools: [
      {
        name: 'pack_weather',
        description: 'Current weather for a city',
        kind: 'http',
        inputSchema: {
          type: 'object',
          properties: { city: { type: 'string' } },
          required: ['city'],
        },
        config: {
          method: 'GET',
          urlTemplate: 'https://api.example.com/weather?city={{input.city}}',
        },
      },
    ],
    skills: [
      {
        name: 'forecaster',
        instructions: 'Use pack_weather and answer in Celsius.',
        tools: ['pack_weather'],
      },
    ],
    mcpServers: [],
  },
  null,
  2,
);

function InstallPluginDialog({ tenantId }: { tenantId: string }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [manifest, setManifest] = useState(EXAMPLE_MANIFEST);
  const [jsonError, setJsonError] = useState<string | null>(null);
  const install = useMutation({
    mutationFn: (parsed: unknown) =>
      api<PluginDto>('/plugins', { method: 'POST', body: { manifest: parsed } }),
    onSuccess: (p) => {
      toast.success(`${p.name} ${p.version} installed`);
      for (const key of ['plugins', 'skills', 'tools', 'mcp-servers', 'marketplace'])
        void queryClient.invalidateQueries({ queryKey: [key, tenantId] });
      setOpen(false);
    },
  });
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) install.reset();
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline">Install from manifest</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Install a plugin</DialogTitle>
          <DialogDescription>
            Plugins are JSON manifests (no code) that add tools, skills and MCP servers in one step.
          </DialogDescription>
        </DialogHeader>
        <ErrorAlert error={install.error} />
        {install.error instanceof ApiError && install.error.errors.length > 0 && (
          <ul className="text-destructive list-disc pl-5 text-sm">
            {install.error.errors.map((e) => (
              <li key={e.path + e.message}>
                <span className="font-mono">{e.path}</span>: {e.message}
              </li>
            ))}
          </ul>
        )}
        <Label htmlFor="plugin-manifest">Manifest</Label>
        <Textarea
          id="plugin-manifest"
          className="min-h-80 font-mono text-xs"
          value={manifest}
          onChange={(e) => setManifest(e.target.value)}
        />
        {jsonError && <p className="text-destructive text-sm">{jsonError}</p>}
        <DialogFooter>
          <Button
            disabled={install.isPending}
            onClick={() => {
              try {
                const parsed: unknown = JSON.parse(manifest);
                setJsonError(null);
                install.mutate(parsed);
              } catch {
                setJsonError('This is not valid JSON.');
              }
            }}
          >
            Install
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

function lineCount(text: string): number {
  return text === '' ? 0 : text.split('\n').length;
}

function SkillsTab({
  tenantId,
  canEdit,
  tools,
}: {
  tenantId: string;
  canEdit: boolean;
  tools: ToolDto[];
}) {
  const queryClient = useQueryClient();
  const skills = useQuery({
    queryKey: ['skills', tenantId],
    queryFn: () => api<{ items: SkillDto[] }>('/skills'),
  });
  const items = skills.data?.items ?? [];
  const search = useListSearch(items, (s) => [s.name, s.description, s.instructions]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const editing = items.find((s) => s.id === editingId) ?? null;
  const toolName = (id: string) => tools.find((t) => t.id === id)?.name ?? 'unknown tool';
  const removeSkill = useMutation({
    mutationFn: (id: string) => api<void>(`/skills/${id}`, { method: 'DELETE' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['skills', tenantId] }),
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <div className="grid gap-4">
      <ListSearch
        label="Search skills"
        placeholder="Search skills by name, description or instructions"
        value={search.query}
        onChange={search.setQuery}
        actions={
          canEdit && (
            <>
              <SkillsFolderImportDialog tenantId={tenantId} />
              <NewSkillDialog tenantId={tenantId} tools={tools} />
            </>
          )
        }
      />
      <ErrorAlert error={skills.error} />
      {!skills.isPending && items.length === 0 ? (
        <EmptyState title="No skills yet">
          Create one, import a skills folder (SKILL.md files), or install a plugin that brings some.
        </EmptyState>
      ) : items.length > 0 && search.filtered.length === 0 ? (
        <NoMatches query={search.query} />
      ) : (
        items.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Instructions</TableHead>
                <TableHead>Tools</TableHead>
                <TableHead>Files</TableHead>
                <TableHead className="w-0">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {search.filtered.map((s) => {
                const readOnly = !canEdit || s.pluginId !== null;
                return (
                  <TableRow key={s.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-medium">{s.name}</span>
                        {s.pluginId && <Badge variant="outline">Plugin</Badge>}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      <span className="block max-w-sm truncate" title={s.description || undefined}>
                        {s.description || '—'}
                      </span>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {plural(lineCount(s.instructions), 'line')}
                    </TableCell>
                    <TableCell
                      className="text-muted-foreground"
                      title={s.toolIds.map(toolName).join(', ') || undefined}
                    >
                      {s.toolIds.length > 0 ? s.toolIds.length : '—'}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {s.files.length > 0 ? s.files.length : '—'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="icon-sm"
                          variant="ghost"
                          aria-label={readOnly ? `View ${s.name}` : `Edit ${s.name}`}
                          title={readOnly ? 'View' : 'Edit'}
                          onClick={() => {
                            setEditingId(s.id);
                            setEditOpen(true);
                          }}
                        >
                          {readOnly ? <Eye aria-hidden /> : <Pencil aria-hidden />}
                        </Button>
                        {canEdit && !s.pluginId && (
                          <Button
                            size="icon-sm"
                            variant="ghost-destructive"
                            aria-label={`Delete ${s.name}`}
                            title="Delete"
                            disabled={removeSkill.isPending && removeSkill.variables === s.id}
                            onClick={() => removeSkill.mutate(s.id)}
                          >
                            <Trash2 aria-hidden />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )
      )}
      <EditSkillDialog
        skill={editing}
        open={editOpen && editing !== null}
        onOpenChange={setEditOpen}
        tools={tools}
        tenantId={tenantId}
        canEdit={canEdit}
      />
    </div>
  );
}

function PluginsTab({
  tenantId,
  canEdit,
  plugins,
}: {
  tenantId: string;
  canEdit: boolean;
  plugins: { data?: { items: PluginDto[] }; error: unknown; isPending: boolean };
}) {
  const queryClient = useQueryClient();
  const items = plugins.data?.items ?? [];
  const search = useListSearch(items, (p) => [p.name, p.description, p.version]);
  const uninstall = useMutation({
    mutationFn: (id: string) => api<void>(`/plugins/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Plugin uninstalled');
      for (const key of ['plugins', 'skills', 'tools', 'mcp-servers', 'marketplace'])
        void queryClient.invalidateQueries({ queryKey: [key, tenantId] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <div className="grid gap-4">
      <ListSearch
        label="Search installed plugins"
        value={search.query}
        onChange={search.setQuery}
        actions={canEdit && <InstallPluginDialog tenantId={tenantId} />}
      />
      <ErrorAlert error={plugins.error} />
      {!plugins.isPending && items.length === 0 ? (
        <EmptyState title="No plugins installed">
          Pick one from the Marketplace tab, or install your own manifest.
        </EmptyState>
      ) : items.length > 0 && search.filtered.length === 0 ? (
        <NoMatches query={search.query} />
      ) : (
        items.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Version</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Contents</TableHead>
                <TableHead>Installed</TableHead>
                <TableHead className="w-0">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {search.filtered.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="font-mono font-medium">{p.name}</TableCell>
                  <TableCell className="text-muted-foreground font-mono text-xs">
                    {p.version}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    <span className="block max-w-sm truncate" title={p.description || undefined}>
                      {p.description || '—'}
                    </span>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {[
                      plural(p.counts.tools, 'tool'),
                      plural(p.counts.skills, 'skill'),
                      plural(p.counts.mcpServers, 'MCP server'),
                    ].join(' · ')}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{formatDate(p.createdAt)}</TableCell>
                  <TableCell>
                    {canEdit && (
                      <div className="flex justify-end">
                        <Button
                          size="icon-sm"
                          variant="ghost-destructive"
                          aria-label={`Uninstall ${p.name}`}
                          title="Uninstall"
                          disabled={uninstall.isPending && uninstall.variables === p.id}
                          onClick={() => uninstall.mutate(p.id)}
                        >
                          <Trash2 aria-hidden />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )
      )}
    </div>
  );
}

function SkillsContent({ tenantId, role }: { tenantId: string; role: Role }) {
  const canEdit = hasRole(role, 'admin');
  const tools = useQuery({
    queryKey: ['tools', tenantId],
    queryFn: () => api<{ items: ToolDto[] }>('/tools'),
  });
  const plugins = useQuery({
    queryKey: ['plugins', tenantId],
    queryFn: () => api<{ items: PluginDto[] }>('/plugins'),
  });

  return (
    <>
      <PageHeader
        title="Skills & Plugins"
        description="Reusable instructions with their tools, and installable bundles of them."
      />
      <Tabs defaultValue="skills">
        <TabsList>
          <TabsTrigger value="skills">Skills</TabsTrigger>
          <TabsTrigger value="marketplace">Marketplace</TabsTrigger>
          <TabsTrigger value="plugins">
            Installed plugins
            {(plugins.data?.items.length ?? 0) > 0 && (
              <Badge variant="secondary" className="ml-1">
                {plugins.data?.items.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="marketplace">
          <PluginMarketplace tenantId={tenantId} canEdit={canEdit} />
        </TabsContent>
        <TabsContent value="skills">
          <SkillsTab tenantId={tenantId} canEdit={canEdit} tools={tools.data?.items ?? []} />
        </TabsContent>
        <TabsContent value="plugins">
          <PluginsTab tenantId={tenantId} canEdit={canEdit} plugins={plugins} />
        </TabsContent>
      </Tabs>
    </>
  );
}

export function SkillsPage() {
  return (
    <RequireTenant>
      {(tenant) => <SkillsContent tenantId={tenant.id} role={tenant.role} />}
    </RequireTenant>
  );
}
